import { isApiError, type ApiError, type ApiErrorBody } from "@gerama/types";

export type Fetcher = (input: string, init?: RequestInit) => Promise<Response>;

export interface ApiClientOptions {
  baseUrl: string;
  /** Reads the bearer token for this request, sync or async (server components read a cookie). */
  getToken?: () => string | null | undefined | Promise<string | null | undefined>;
  /** Called once per request with a 401 — the caller clears session state and redirects to login. */
  onUnauthorized?: () => void;
  /**
   * Tried first on a 401 for a request that carried a token: exchange it at
   * `POST /auth/refresh` (accepted for a grace period past expiry), store
   * the new one, and return it (or null on failure). The request is then
   * retried once; concurrent 401s share one refresh.
   */
  refreshToken?: () => Promise<string | null>;
  fetch?: Fetcher;
  /** Headers sent with every request (e.g. the console's `X-Provider-Id`), read per request. */
  headers?: () => Record<string, string>;
  /** Language for backend error messages (`X-Lang`); both UIs are Persian, so it defaults to `fa`. */
  lang?: string;
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  /** Set for `multipart/form-data` uploads — body must already be a `FormData`. */
  isFormData?: boolean;
  headers?: Record<string, string>;
}

function buildQueryString(query?: RequestOptions["query"]): string {
  if (!query) return "";
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

/**
 * Normalizes any failure — a structured `ApiErrorBody` from
 * `HttpExceptionFilter`, a plain-text error body, or a network failure —
 * into the single `ApiError` shape the rest of the app relies on (skill
 * section 11: "Error Architecture").
 */
async function toApiError(response: Response): Promise<ApiError> {
  let body: ApiErrorBody | undefined;
  try {
    body = (await response.json()) as ApiErrorBody;
  } catch {
    // non-JSON error body (e.g. a gateway timeout page) — fall through
  }

  const fieldErrors = body?.data?.errors?.reduce<Record<string, string[]>>((acc, item) => {
    acc[item.field] = item.messages;
    return acc;
  }, {});

  return {
    code: body?.key ?? String(response.status),
    message: body?.message ?? response.statusText ?? "Request failed",
    statusCode: response.status,
    fieldErrors: fieldErrors && Object.keys(fieldErrors).length > 0 ? fieldErrors : undefined,
  };
}

/**
 * The backend's app-services catch usecase errors and return them as a 2xx
 * `DefaultResponse` with `response.status: "error"` (and no `data`), not as
 * an HTTP error. Without this, every caller's `.then(res => res.data)` would
 * resolve to `undefined` and the mutation would report success.
 */
function toSoftApiError(body: unknown): ApiError | null {
  const meta = (
    body as {
      response?: { status?: string; message?: string; key?: string; statusCode?: number };
    } | null
  )?.response;
  if (!meta || meta.status !== "error") return null;
  // `message` is already translated server-side; `key` is the raw i18n key.
  const message = meta.message || "Request failed";
  return { code: meta.key ?? message, message, statusCode: meta.statusCode ?? 400 };
}

/**
 * For reads where a specific "doesn't exist" error is a normal empty state
 * (no active plan, no lyrics yet): resolve those codes to `null`, rethrow
 * everything else.
 */
export function nullOn(...codes: string[]) {
  return (error: unknown): null => {
    if (isApiError(error) && codes.includes(error.code)) return null;
    throw error;
  };
}

/**
 * A minimal, framework-free typed client factory. Each backend (`admin-api`,
 * `core-api`) gets its own instance via `createApiClient`; resource modules
 * (`songs.api.ts`, `mediaJobs.api.ts`, ...) call `client.request(...)`
 * rather than talking to `fetch` directly, so auth/error handling stays in
 * one place.
 */
export function createApiClient(options: ApiClientOptions) {
  const doFetch = options.fetch ?? fetch;
  let refreshing: Promise<string | null> | null = null;

  async function request<T>(path: string, init: RequestOptions = {}, retried = false): Promise<T> {
    const token = await options.getToken?.();
    const headers: Record<string, string> = {
      Accept: "application/json",
      "X-Lang": options.lang ?? "fa",
      ...options.headers?.(),
      ...init.headers,
    };
    if (!init.isFormData) headers["Content-Type"] = "application/json";
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await doFetch(`${options.baseUrl}${path}${buildQueryString(init.query)}`, {
      method: init.method ?? "GET",
      headers,
      body: init.body
        ? init.isFormData
          ? (init.body as FormData)
          : JSON.stringify(init.body)
        : undefined,
      signal: init.signal,
      cache: "no-store",
    });

    if (
      response.status === 401 &&
      token &&
      !retried &&
      options.refreshToken &&
      path !== "/auth/refresh"
    ) {
      // Another request may already have refreshed since this one was sent.
      const current = await options.getToken?.();
      const fresh =
        current && current !== token
          ? current
          : await (refreshing ??= options
              .refreshToken()
              .catch(() => null)
              .finally(() => {
                refreshing = null;
              }));
      if (fresh) return request<T>(path, init, true);
    }

    if (response.status === 401) {
      options.onUnauthorized?.();
    }

    if (!response.ok) {
      throw await toApiError(response);
    }

    if (response.status === 204) return undefined as T;
    const json = (await response.json()) as T;
    const softError = toSoftApiError(json);
    if (softError) throw softError;
    return json;
  }

  return { request, baseUrl: options.baseUrl };
}

export type ApiClient = ReturnType<typeof createApiClient>;
